import { useMemo } from "react";

import { PayoutSortBy, PayoutSortOrder } from "../../gql-generated/types";
import { formatMilliUsd } from "../../lens-creator-payout/utils/formatMilliUsd";
import { makePayoutDateRange } from "../../lens-insights/converters";
import { usePayoutStatsLensList, useTimeRange } from "../../lens-insights/hooks";
import { LensGranularity } from "../../lens-insights/models";

import { type TopLensRow } from "./useMergeTopLensesWithInfo";

const TOP_LENSES_LIMIT = 5;

export const useTopLensesByPayout = (): { data: TopLensRow[]; loading: boolean } => {
    const { selectedTimeRange } = useTimeRange({ defaultRange: LensGranularity.Lifetime });
    const { startDate, endDate } = useMemo(() => makePayoutDateRange(selectedTimeRange), [selectedTimeRange]);

    const { lenses, loading } = usePayoutStatsLensList({
        startDate,
        endDate,
        countryFilters: [],
        sortBy: PayoutSortBy.SortByTotalAmountMilliUsd,
        sortOrder: PayoutSortOrder.SortOrderDesc,
        limit: TOP_LENSES_LIMIT,
    });

    const data = useMemo<TopLensRow[]>(
        () =>
            lenses.reduce<TopLensRow[]>((acc, rewardedLens) => {
                const lens = rewardedLens.lens;
                if (lens) {
                    acc.push({
                        id: lens.id,
                        name: lens.name,
                        icon: lens.iconUrl,
                        value: formatMilliUsd(rewardedLens.totalEarningsMilliUsd),
                        orgId: lens.organizationId,
                        lensFolderId: lens.lensAccountId?.lensSpaceId ?? undefined,
                    });
                }
                return acc;
            }, []),
        [lenses],
    );

    return { data, loading };
};
